import React, { useState } from 'react';
import { Form, Input, Button, Typography, message, Select } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createFreshDeskTicket } from '../../services/freshdeskService';
import styles from './Contact.module.css';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const Contact = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phonePrefix, setPhonePrefix] = useState('+90');

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    
    try {
      console.log('📝 Contact form submitted:', values);
      
      // Prepare ticket data for new help-center API
      const ticketData = {
        subject: `Contact Form: ${values.firstName} ${values.lastName}`,
        description: values.message || 'No message provided',
        requester_name: `${values.firstName} ${values.lastName}`,
        requester_email: values.email,
        requester_phone: values.phone ? `${phonePrefix}${values.phone}` : ''
      };

      // Create FreshDesk ticket
      const ticketResult = await createFreshDeskTicket(ticketData);
      
      if (ticketResult.success) {
        const ticketId = ticketResult.ticket_id || ticketResult.data?.id || ticketResult.data?.ticket_id;
        message.success(
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
              {t('contactPage.successTitle')}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              Ticket ID: <strong style={{ color: '#1890ff' }}>#{ticketId}</strong>
            </div>
            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
              {t('contactPage.successMessage')}
            </div>
          </div>,
          8 // 8 saniye göster
        );
        
        // Reset form
        form.resetFields();
      } else {
        throw new Error('Failed to create ticket');
      }
      
    } catch (error) {
      console.error('❌ Contact form error:', error);
      message.error(t('contactPage.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.headerSection}>
        <div className={styles.headerContent}>
          <Title level={1} className={styles.pageTitle}>
            {t('contactPage.title')}
          </Title>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.contentGrid}>
          
          {/* Left Side - Contact Form */}
          <div className={styles.formCard}>
            <Title level={3} className={styles.formTitle}>
              {t('contactPage.subtitle')}
            </Title>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className={styles.form}
            >
              {/* Name Row */}
              <div className={styles.nameRow}>
                <Form.Item
                  name="firstName"
                  rules={[{ required: true, message: t('contactPage.validation.firstNameRequired') }]}
                  className={styles.formItem}
                >
                  <Input 
                    size="large" 
                    placeholder={t('contactPage.firstName')}
                    className={styles.input}
                  />
                </Form.Item>

                <Form.Item
                  name="lastName"
                  rules={[{ required: true, message: t('contactPage.validation.lastNameRequired') }]}
                  className={styles.formItem}
                >
                  <Input 
                    size="large" 
                    placeholder={t('contactPage.lastName')}
                    className={styles.input}
                  />
                </Form.Item>
              </div>

              {/* Email */}
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: t('contactPage.validation.emailRequired') },
                  { type: 'email', message: t('contactPage.validation.emailInvalid') }
                ]}
                className={styles.formItem}
              >
                <Input 
                  size="large" 
                  placeholder={t('contactPage.email')}
                  className={styles.input}
                />
              </Form.Item>

              {/* Message */}
              <Form.Item
                name="message"
                rules={[
                  { required: true, message: t('contactPage.validation.messageRequired') },
                  { min: 20, message: t('contactPage.validation.messageMinLength') }
                ]}
                className={styles.formItem}
              >
                <TextArea
                  rows={6}
                  placeholder={t('contactPage.message')}
                  className={styles.textarea}
                  maxLength={2000}
                  showCount
                />
              </Form.Item>

              {/* Phone */}
              <Form.Item
                name="phone"
                className={styles.formItem}
              >
                <Input 
                  size="large" 
                  placeholder={t('contactPage.phone')}
                  className={styles.input}
                  addonBefore={
                    <Select 
                      defaultValue="+90" 
                      className={styles.phoneSelect}
                      size="large"
                      onChange={(value) => setPhonePrefix(value)}
                    >
                      <Option value="+90">🇹🇷 +90</Option>
                      <Option value="+1">🇺🇸 +1</Option>
                      <Option value="+44">🇬🇧 +44</Option>
                      <Option value="+49">🇩🇪 +49</Option>
                      <Option value="+33">🇫🇷 +33</Option>
                      <Option value="+39">🇮🇹 +39</Option>
                      <Option value="+34">🇪🇸 +34</Option>
                      <Option value="+31">🇳🇱 +31</Option>
                      <Option value="+46">🇸🇪 +46</Option>
                      <Option value="+47">🇳🇴 +47</Option>
                    </Select>
                  }
                />
              </Form.Item>

              {/* Submit Button */}
              <Form.Item className={styles.submitItem}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={isSubmitting}
                  className={styles.submitButton}
                >
                  {isSubmitting ? t('contactPage.sending') : t('contactPage.sendMessage')}
                </Button>
              </Form.Item>
            </Form>
          </div>

          {/* Right Side - Talk with us */}
          <div className={styles.infoCard}>
            <Title level={2} className={styles.infoTitle}>
              {t('contactPage.talkWithUs')}
            </Title>
            <Paragraph className={styles.infoText}>
              {t('contactPage.callUs')}{' '}
              <a href="tel:+908502427077" className={styles.phoneLink}>
                +90 850 242 70 77
              </a>
            </Paragraph>
            <Paragraph className={styles.infoText}>
              {t('contactPage.orMessageUs')}{' '}
              <a href="https://wa.me/908502427077" target="_blank" rel="noopener noreferrer" className={styles.whatsappLink}>
                WhatsApp
              </a>
            </Paragraph>
          </div>
        </div>
      </div>

      {/* Offices Section */}
      <div className={styles.officesSection}>
        <div className={styles.officesContainer}>
          <Title level={2} className={styles.officesTitle}>
            {t('contactPage.officesTitle')}
          </Title>

          <div className={styles.officesGrid}>
            {/* UK Office */}
            <div className={styles.officeCard}>
              <Title level={4} className={styles.officeTitle}>
                {t('contactPage.ukOffice')}
              </Title>
              <Paragraph className={styles.officeAddress}>
                {t('contactPage.ukAddress').split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < 2 && <br />}
                  </React.Fragment>
                ))}
              </Paragraph>
              <div className={styles.officeEmail}>
                <MailOutlined className={styles.emailIcon} />
                <a href="mailto:info@tourzone.com" className={styles.emailLink}>
                  info@tourzone.com
                </a>
              </div>
            </div>

            {/* Turkey Office */}
            <div className={styles.officeCard}>
              <Title level={4} className={styles.officeTitle}>
                {t('contactPage.turkeyOffice')}
              </Title>
              <Paragraph className={styles.officeAddress}>
                {t('contactPage.turkeyAddress').split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < 2 && <br />}
                  </React.Fragment>
                ))}
              </Paragraph>
              <div className={styles.officeEmail}>
                <MailOutlined className={styles.emailIcon} />
                <a href="mailto:info@tourzone.com" className={styles.emailLink}>
                  info@tourzone.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
